import { z } from "zod";
import type {
  CrmContact,
  CrmRecordType,
  CrmRecordTypeFieldName,
  CrmRecordTypeFields,
} from "@/lib/api/crm/masters";
import type { CrmRules } from "@/lib/api/crm/rules";
import { displayPercentToMarketingFee, marketingFeeToDisplayPercent } from "@/lib/customer";
import { isSyncedToRisk } from "../recordFields";
import { portalDate, strictDecimalString } from "@/lib/validation/common";
import {
  crmContactValueIssue,
  type CrmRecordTypeFieldsRequest,
} from "@/lib/validation/crm";
import { onePerContactKindMessage } from "./tagOptions";

/**
 * The type fields the form edits, as it holds them: text inputs are strings ("" = blank).
 * Not edited directly: harvest_iq_profile is always sent from "Sync to risk" (see `toRecordRequest`).
 * Never sent (the API keeps their stored values): force_show_activities. The onboarding fields
 * partner_names, shirt_sizes, original_acres and bol_sequence are only sent on create, from the
 * picked onboarding instance (`RecordFormValues.onboarding`).
 */
export interface RecordFormTypeFields {
  notes: string;
  shipping_instructions: string;
  regular_hours: string;
  current_week_hours: string;
  next_week_hours: string;
  /** Percentage as typed ("1.5"); stored as a fraction (0.015). */
  marketing_fee: string;
  /** The one account code picked, like ManageCustomers' RJO account number. */
  active_account_codes: string[];
  /** Shown as the "Sync to risk" checkbox, inverted: false only while synced (`isSyncedToRisk`). */
  skip_harvest_iq_sync: boolean;
  preferred_defer_payment_type: "" | "ach" | "check";
  deferred_payment_lien_status: "" | "active";
  /** YYYY-MM-DD, or "". */
  signed_up_date: string;
  is_commercial: boolean;
  /** Trader id, or "" for none. */
  trader_id: string;
}

export type RecordFormFieldName = keyof RecordFormTypeFields;

export interface ContactFormRow {
  /** null for a contact not saved yet. */
  id: string | null;
  kind: CrmContact["kind"];
  value: string;
  label: string;
  tag_ids: string[];
}

export interface AddressFormRow {
  /** null for an address not saved yet. */
  id: string | null;
  label: string;
  address_line_one: string;
  address_line_two: string;
  city: string;
  state: string;
  zipcode: string;
  county: string;
  country: string;
  tag_ids: string[];
}

export const addressTextFields = [
  "label",
  "address_line_one",
  "address_line_two",
  "city",
  "state",
  "zipcode",
  "county",
  "country",
] as const satisfies readonly (keyof AddressFormRow)[];

/** The onboarding instance picked on create, and its values the form doesn't show (sent as is). */
export interface OnboardingSelection {
  instance_id: string;
  partner_names: string[];
  shirt_sizes: string[];
  original_acres: number | null;
  bol_sequence: string | null;
}

/** A record group created together with the record. */
export interface NewGroupValues {
  long_name: string;
  /** "" = same as the name. */
  short_name: string;
}

export interface RecordFormValues {
  /** The existing group; "" while `new_group` is set. */
  master_id: string;
  /** Create only, where the form allows a new group; null = the record goes into `master_id`. */
  new_group: NewGroupValues | null;
  record_type: CrmRecordType | "";
  /** "" = none. */
  owner_id: string;
  /** Create only, for types GET /crm/rules marks requires_client_id; "" otherwise. Never sent on edit. */
  client_id: string;
  /** Create only, for types GET /crm/rules marks creates_customer; null otherwise. Never sent on edit. */
  onboarding: OnboardingSelection | null;
  long_name: string;
  short_name: string;
  /** Record tags. */
  tag_ids: string[];
  /** Only the fields GET /crm/rules gives the record type. */
  fields: Partial<RecordFormTypeFields>;
  contacts: ContactFormRow[];
  addresses: AddressFormRow[];
}

export type RecordFieldInput =
  | "text"
  | "textarea"
  | "percent"
  | "date"
  | "select"
  | "accountCode"
  | "syncToRisk"
  | "switch"
  | "trader";

type RequestValue<K extends CrmRecordTypeFieldName> = Exclude<CrmRecordTypeFieldsRequest[K], undefined>;

interface RecordFormField<FormValue, RecordValue, RequestFieldValue> {
  input: RecordFieldInput;
  empty: FormValue;
  schema: z.ZodType<FormValue>;
  /** `record` is the whole stored record, for values derived from more than one field. */
  fromRecord: (value: RecordValue, record: CrmRecordTypeFields) => FormValue;
  toRequest: (value: FormValue) => RequestFieldValue;
  /** Choices for "select" inputs ("" = none is always offered). */
  options?: readonly string[];
}

type RecordFormFieldFor<K extends RecordFormFieldName> =
  RecordFormField<RecordFormTypeFields[K], CrmRecordTypeFields[K], RequestValue<K>>;

const isBlank = (value: string): boolean => value.trim() === "";

function textField(input: "text" | "textarea"): RecordFormField<string, string | null | undefined, string | null> {
  return {
    input,
    empty: "",
    schema: z.string(),
    fromRecord: (value) => value ?? "",
    toRequest: (value) => value.trim() || null,
  };
}

const switchField: RecordFormField<boolean, boolean | undefined, boolean> = {
  input: "switch",
  empty: false,
  schema: z.boolean(),
  fromRecord: (value) => value ?? false,
  toRequest: (value) => value,
};

/** How each edited type field is shown, validated and sent. Which fields a type has comes from GET /crm/rules. */
export const recordFormFields: { [K in RecordFormFieldName]: RecordFormFieldFor<K> } = {
  notes: textField("textarea"),
  shipping_instructions: textField("text"),
  regular_hours: textField("text"),
  current_week_hours: textField("text"),
  next_week_hours: textField("text"),
  marketing_fee: {
    input: "percent",
    empty: "",
    schema: z.string().refine((value) => isBlank(value) || strictDecimalString.safeParse(value.trim()).success, "Enter a percentage like 1.5"),
    fromRecord: (value) => marketingFeeToDisplayPercent(value),
    toRequest: (value) => (isBlank(value) ? null : displayPercentToMarketingFee(value.trim())),
  },
  active_account_codes: {
    input: "accountCode",
    empty: [],
    schema: z.array(z.string()),
    fromRecord: (value) => value ?? [],
    toRequest: (value) => value,
  },
  skip_harvest_iq_sync: {
    input: "syncToRisk",
    // A new record isn't synced to risk until it's ticked.
    empty: true,
    schema: z.boolean(),
    // A blank HarvestIQ profile means not synced, whatever skip_harvest_iq_sync says.
    fromRecord: (_value, record) => !isSyncedToRisk(record),
    toRequest: (value) => value,
  },
  preferred_defer_payment_type: {
    input: "select",
    empty: "",
    schema: z.enum(["", "ach", "check"]),
    fromRecord: (value) => value ?? "",
    toRequest: (value) => (value === "" ? null : value),
    options: ["ach", "check"],
  },
  deferred_payment_lien_status: {
    input: "select",
    empty: "",
    schema: z.enum(["", "active"]),
    fromRecord: (value) => value ?? "",
    toRequest: (value) => (value === "" ? null : value),
    options: ["active"],
  },
  signed_up_date: {
    input: "date",
    empty: "",
    schema: z.string().refine((value) => value === "" || portalDate.safeParse(value).success, "Enter a valid date"),
    fromRecord: (value) => value ?? "",
    toRequest: (value) => value || null,
  },
  is_commercial: switchField,
  trader_id: {
    input: "trader",
    empty: "",
    schema: z.string(),
    fromRecord: (value) => value ?? "",
    toRequest: (value) => value || null,
  },
};

function isFormField(field: CrmRecordTypeFieldName): field is RecordFormFieldName {
  return field in recordFormFields;
}

/** The record type's fields (from GET /crm/rules) that the form edits; none until a type is chosen. */
export function recordTypeFields(rules: CrmRules, recordType: CrmRecordType | ""): RecordFormFieldName[] {
  return recordType ? rules.record_types[recordType].fields.filter(isFormField) : [];
}

function fieldSchemas(fields: RecordFormFieldName[]): z.ZodRawShape {
  return Object.fromEntries(fields.map((field) => [field, recordFormFields[field].schema]));
}

const tagIdsSchema = z.array(z.string()).min(1, "Choose at least one tag");

const contactRowSchema = z.object({
  id: z.string().nullable(),
  kind: z.enum(["phone", "email"]),
  value: z.string(),
  label: z.string(),
  tag_ids: tagIdsSchema,
}).superRefine((row, context) => {
  const message = isBlank(row.value)
    ? `Enter ${row.kind === "phone" ? "a phone number" : "an email address"}`
    : crmContactValueIssue(row.kind, row.value);
  if (message) context.addIssue({ code: z.ZodIssueCode.custom, message, path: ["value"] });
});

const addressRowSchema = z.object({
  id: z.string().nullable(),
  label: z.string(),
  address_line_one: z.string(),
  address_line_two: z.string(),
  city: z.string(),
  state: z.string(),
  zipcode: z.string(),
  county: z.string(),
  country: z.string(),
  tag_ids: tagIdsSchema,
});

/** Whether the create form asks for a client ID: the chosen type must be created with one. */
export function requiresClientId(rules: CrmRules, recordType: CrmRecordType | ""): boolean {
  return recordType ? rules.record_types[recordType].requires_client_id : false;
}

/** Whether the create form offers an onboarding instance: the chosen type creates a customer. */
export function createsCustomer(rules: CrmRules, recordType: CrmRecordType | ""): boolean {
  return recordType ? rules.record_types[recordType].creates_customer : false;
}

const onboardingSchema = z.object({
  instance_id: z.string(),
  partner_names: z.array(z.string()),
  shirt_sizes: z.array(z.string()),
  original_acres: z.number().nullable(),
  bol_sequence: z.string().nullable(),
}).nullable();

/** The form's schema for the chosen record type: only that type's fields exist in it. */
export function buildRecordFormSchema(rules: CrmRules, recordType: CrmRecordType | "", mode: "create" | "update") {
  const ownerTypes = recordType ? rules.record_types[recordType].owner_types : null;
  return z.object({
    master_id: z.string(),
    new_group: z.object({
      long_name: z.string().refine((value) => !isBlank(value), "Enter a group name"),
      short_name: z.string(),
    }).nullable(),
    record_type: z.string().min(1, "Choose a type"),
    owner_id: ownerTypes ? z.string().min(1, "Choose a record this belongs to") : z.string(),
    client_id: mode === "create" && requiresClientId(rules, recordType)
      ? z.string().refine((value) => !isBlank(value), "Enter a client ID")
      : z.string(),
    onboarding: onboardingSchema,
    long_name: z.string().refine((value) => !isBlank(value), "Enter a name"),
    short_name: z.string(),
    tag_ids: z.array(z.string()),
    fields: z.object(fieldSchemas(recordTypeFields(rules, recordType))),
    contacts: z.array(contactRowSchema).superRefine((rows, context) => {
      // GET /crm/rules: a one-per-contact-kind tag (e.g. Primary) may sit on only one contact of each kind.
      rows.forEach((row, index) => {
        const duplicated = row.tag_ids.filter((tagId) =>
          rules.tags[tagId]?.one_per_contact_kind &&
          rows.some((other, otherIndex) => otherIndex !== index && other.kind === row.kind && other.tag_ids.includes(tagId)));
        if (duplicated.length > 0) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: onePerContactKindMessage(rules, row.kind, duplicated),
            path: [index, "tag_ids"],
          });
        }
      });
    }),
    addresses: z.array(addressRowSchema),
  }).superRefine((values, context) => {
    if (values.new_group === null && values.master_id === "") {
      context.addIssue({ code: z.ZodIssueCode.custom, message: "Choose a record group", path: ["master_id"] });
    }
  });
}
